import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  clearCourseRouteGeometryUnlessManualPolyline,
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  inheritLegMetricsForManualCourseChange,
  IStationCourseProps,
  RebuildRouteTypeEnum,
  removeStationFromCourseDtoMetadata,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useCourseDtoActions } from "../../hooks";

export type IUseCourseStation = ReturnType<typeof useCourseStation>;

export const useCourseStation = (props: IStationCourseProps) => {
  const { station, courseDto } = props;
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { doNotAutoUpdateCourses } = useSystemParams();
  const { updateEditingCourseMetadata, applyEditingCourseMetadataLocally } =
    useCourseDtoActions();
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );
  const removeCourseStation = useEvent(async (): Promise<void> => {
    const metadataRecounted = removeStationFromCourseDtoMetadata({
      courseDto,
      stationsToRemoveIds: [station.id],
      timezone,
    });
    // Manual mode skips the backend route recalc; drop the removed station's leg so durations stay consistent
    // with the station count (course production requires stationsInfo.length === durations.length + 1).
    const hasManualPolyline = Boolean(
      courseDto.metadata?.[CourseMetadataKeys.isManualPolylineEdit],
    );
    const metadataToSave =
      doNotAutoUpdateCourses || hasManualPolyline
        ? inheritLegMetricsForManualCourseChange({
            previousStationsInfo: courseDto.metadata?.stationsInfo || [],
            metadata: metadataRecounted,
          })
        : metadataRecounted;
    const metadataForUpdate = clearCourseRouteGeometryUnlessManualPolyline({
      metadata: metadataToSave,
      doNotAutoUpdateCourses,
    });

    applyEditingCourseMetadataLocally({
      metadata: metadataForUpdate,
      skipReorderStationsByTime: true,
    });
    setCourseHasUnsavedStationChange(true);

    const result = await updateEditingCourseMetadata({
      metadata: metadataForUpdate,
      optimize: false,
      skipApiCheck: hasManualPolyline,
      rebuildType: RebuildRouteTypeEnum.KeepOrder,
      skipReorderStationsByTime: true,
    });
    if (!result.success) {
      warnAndToastSimpleResult(result, t);
    }
  });

  return {
    removeCourseStation,
  };
};
